import React, { useState, useEffect, useRef } from 'react';
import { ComplianceCompanyConfig } from '@/constants/compliance-companies';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Volume2,
  User,
  Building2,
  Clock,
} from 'lucide-react';
import { useComplianceAgentCall } from '@/hooks/useComplianceAgentCall';

interface ComplianceCallProps {
  config: ComplianceCompanyConfig;
  onEndCall: (
    duration: number,
    transcript?: Array<{
      source: 'ai' | 'user';
      message: string;
      timestamp: Date;
    }>,
  ) => void;
}

export const ComplianceCall: React.FC<ComplianceCallProps> = ({
  config,
  onEndCall,
}) => {
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [transcript, setTranscript] = useState<
    Array<{
      source: 'ai' | 'user';
      message: string;
      timestamp: Date;
    }>
  >([]);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<number>(Date.now());

  const {
    isConnected,
    toggleAudio,
    endCall: endAgentCall,
    startCall,
    isSpeaking,
  } = useComplianceAgentCall({
    agentId: config.agentId,
    systemPrompt: config.systemPrompt,
    onTranscript: (message: string, source: 'ai' | 'user') => {
      setTranscript((prev) => [
        ...prev,
        {
          source,
          message,
          timestamp: new Date(),
        },
      ]);
    },
  });

  // Start call on mount
  useEffect(() => {
    startCall();
    return () => {
      // Cleanup on unmount
      endAgentCall();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update call duration
  useEffect(() => {
    const interval = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcript]);

  const handleToggleMute = () => {
    setIsMuted(!isMuted);
    toggleAudio();
  };

  const handleEndCall = () => {
    endAgentCall();
    onEndCall(callDuration, transcript);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={config.logo} alt={config.name} className="h-10" />
            <div className="border-l border-gray-300 pl-4">
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    isConnected ? 'bg-green-500 animate-pulse' : 'bg-amber-500'
                  }`}
                />
                <span className="text-sm font-medium text-gray-700">
                  {isConnected ? 'Call Active' : 'Connecting...'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Powered by</span>
            <img src={config.hupoLogo} alt="Hupo" className="h-6" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Call Interface */}
          <div className="lg:col-span-2">
            <Card className="bg-white p-8">
              {/* Participant Info */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {config.scenario.participants.client.name}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {config.scenario.participants.client.role}
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        <Building2 className="w-3 h-3 text-gray-400" />
                        <p className="text-xs text-gray-500">
                          {config.scenario.participants.client.company}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Call Timer */}
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span className="text-2xl font-mono font-semibold">
                        {formatDuration(callDuration)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Audio Visualizer */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="flex justify-center items-center">
                    <div className="flex items-end gap-1 h-16">
                      {[...Array(30)].map((_, i) => (
                        <div
                          key={i}
                          className={`w-1 bg-gradient-to-t rounded-full transition-all duration-300 ${
                            isSpeaking
                              ? 'from-blue-600 to-blue-400 animate-pulse'
                              : 'from-gray-300 to-gray-200'
                          }`}
                          style={{
                            height: isSpeaking
                              ? `${
                                  Math.sin(i * 0.3 + Date.now() * 0.002) * 30 +
                                  35
                                }px`
                              : '8px',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Call Status */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Compliance Training Call
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        Scenario: {config.scenario.title}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Call Controls */}
                <div className="flex justify-center gap-6">
                  <Button
                    variant={isMuted ? 'destructive' : 'outline'}
                    size="lg"
                    className={`rounded-full w-16 h-16 transition-all ${
                      isMuted ? 'bg-red-500 hover:bg-red-600 text-white' : ''
                    }`}
                    onClick={handleToggleMute}
                  >
                    {isMuted ? (
                      <MicOff className="w-6 h-6" />
                    ) : (
                      <Mic className="w-6 h-6" />
                    )}
                  </Button>

                  <Button
                    size="lg"
                    className="rounded-full w-20 h-20 bg-red-500 hover:bg-red-600 text-white"
                    onClick={handleEndCall}
                  >
                    <PhoneOff className="w-8 h-8" />
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="rounded-full w-16 h-16"
                    disabled
                  >
                    <Volume2 className="w-6 h-6" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Live Transcript */}
          <div>
            <Card className="bg-white h-full flex flex-col max-h-[700px]">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Live Transcript
                </h3>
                <p className="text-sm text-gray-600">
                  Monitor compliance interactions
                </p>
              </div>

              <div ref={transcriptRef} className="flex-1 p-4 overflow-y-auto">
                {transcript.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <Phone className="w-12 h-12 mb-3 animate-pulse" />
                    <p className="text-sm">Waiting for conversation...</p>
                    <p className="text-xs mt-1">Speak clearly when prompted</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transcript.map((entry, index) => (
                      <div
                        key={index}
                        className={`relative ${
                          entry.source === 'ai' ? '' : 'pl-4'
                        }`}
                      >
                        <div
                          className={`p-3 rounded-lg ${
                            entry.source === 'ai'
                              ? 'bg-gray-100 border border-gray-200'
                              : 'bg-blue-50 border border-blue-200'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                entry.source === 'ai'
                                  ? 'bg-gray-300'
                                  : 'bg-blue-500'
                              }`}
                            >
                              <User className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-xs font-medium text-gray-700">
                              {entry.source === 'ai'
                                ? config.scenario.participants.client.name
                                : 'You'}
                            </span>
                            <span className="text-xs text-gray-500 ml-auto">
                              {entry.timestamp.toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {entry.message}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
